import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ImagePlus, Images, RefreshCw, Repeat, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { SALES } from "../data/sales";

export function AdminSaleGallery() {
  const [saleId, setSaleId] = useState(SALES[0]?.id || "");
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [captionEdits, setCaptionEdits] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const replaceInputRef = useRef(null);

  const load = useCallback(async (sid) => {
    setLoading(true);
    try {
      const { data } = await api.get("/sale-gallery", { params: { sale_id: sid } });
      setPhotos(data);
      setCaptionEdits({});
      setConfirmDelete(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (saleId) load(saleId);
  }, [saleId, load]);

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      await api.post(`/sale-gallery/${saleId}/photos`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      toast.success(`${files.length} photo${files.length === 1 ? "" : "s"} added — live on the site now`);
      setFiles([]);
      await load(saleId);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleMove = async (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= photos.length) return;
    const reordered = [...photos];
    [reordered[index], reordered[j]] = [reordered[j], reordered[index]];
    setPhotos(reordered);
    try {
      await api.post(`/sale-gallery/${saleId}/reorder`, { photo_ids: reordered.map((p) => p.id) });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      await load(saleId);
    }
  };

  const handleSaveCaption = async (photo) => {
    const caption = captionEdits[photo.id] ?? photo.caption;
    try {
      await api.patch(`/sale-gallery/photos/${photo.id}`, { caption });
      setPhotos((ps) => ps.map((p) => (p.id === photo.id ? { ...p, caption } : p)));
      setCaptionEdits((ce) => { const n = { ...ce }; delete n[photo.id]; return n; });
      toast.success("Caption saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleReplacePicked = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file || !replaceTarget) return;
    try {
      const form = new FormData();
      form.append("file", file);
      await api.put(`/sale-gallery/photos/${replaceTarget}/image`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      toast.success("Photo replaced");
      await load(saleId);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setReplaceTarget(null);
    }
  };

  const handleDelete = async (photo) => {
    if (confirmDelete !== photo.id) {
      setConfirmDelete(photo.id);
      return;
    }
    try {
      await api.delete(`/sale-gallery/photos/${photo.id}`);
      toast.success("Photo removed from the gallery");
      setConfirmDelete(null);
      await load(saleId);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setConfirmDelete(null);
    }
  };

  return (
    <div className="mt-6 bg-white border border-linen" data-testid="admin-gallery-panel">
      <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-linen">
        <div>
          <h2 className="text-2xl font-medium text-ink flex items-center gap-2">
            <Images className="w-5 h-5 text-brass" /> Sale Photo Gallery
          </h2>
          <p className="mt-1 text-sm text-ink/60" data-testid="admin-gallery-count">
            {photos.length} photo{photos.length === 1 ? "" : "s"} · changes appear on the public page immediately
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="form-input !w-auto"
            value={saleId}
            onChange={(e) => setSaleId(e.target.value)}
            data-testid="admin-gallery-sale-select"
            aria-label="Choose which sale to manage"
          >
            {SALES.map((s) => (
              <option key={s.id} value={s.id}>{s.title}</option>
            ))}
          </select>
          <button onClick={() => load(saleId)} className="btn-outline-dark text-xs px-4 py-2.5" data-testid="admin-gallery-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="px-6 py-6 border-b border-linen bg-sand/60">
        <p className="form-label">Add photographs</p>
        <p className="text-xs text-ink/50 mb-3">JPG, PNG, WEBP or HEIC up to 10 MB each. Select several at once — up to 150 photos per sale.</p>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="btn-outline-dark text-xs px-4 py-2.5 cursor-pointer inline-flex items-center gap-2" data-testid="admin-gallery-choose-button">
            <ImagePlus className="w-4 h-4" /> Choose photos
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              data-testid="admin-gallery-file-input"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
          </label>
          {files.length > 0 && (
            <p className="text-sm text-ink/70" data-testid="admin-gallery-selected-count">
              {files.length} selected: {files.slice(0, 3).map((f) => f.name).join(", ")}{files.length > 3 ? ` +${files.length - 3} more` : ""}
            </p>
          )}
          <button
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
            className="btn-brass text-xs px-5 py-2.5 disabled:opacity-50"
            data-testid="admin-gallery-upload-button"
          >
            {uploading ? "Uploading…" : `Upload ${files.length > 0 ? files.length : ""} photo${files.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>

      {photos.length === 0 && !loading && (
        <div className="p-12 text-center" data-testid="admin-gallery-empty">
          <Images className="w-10 h-10 text-ink/30 mx-auto" />
          <p className="mt-4 text-ink/60">No photos in this sale's gallery yet. Upload the first batch above.</p>
        </div>
      )}

      <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {photos.map((p, i) => {
          const edited = captionEdits[p.id] !== undefined && captionEdits[p.id] !== p.caption;
          return (
            <div key={p.id} className="border border-linen bg-white" data-testid={`admin-gallery-photo-${p.id}`}>
              <div className="relative">
                <img src={p.image_url} alt={p.caption || `Gallery photo ${i + 1}`} className="w-full aspect-square object-cover" loading="lazy" />
                <span className="absolute top-2 left-2 bg-forest text-ivory text-[0.65rem] font-bold px-2 py-1">#{i + 1}</span>
              </div>
              <div className="p-3 space-y-3">
                <div className="flex gap-2">
                  <input
                    className="form-input !py-2 text-xs flex-1"
                    placeholder="Optional caption"
                    value={captionEdits[p.id] ?? p.caption}
                    maxLength={200}
                    onChange={(e) => setCaptionEdits((ce) => ({ ...ce, [p.id]: e.target.value }))}
                    data-testid={`admin-gallery-caption-input-${p.id}`}
                  />
                  {edited && (
                    <button
                      onClick={() => handleSaveCaption(p)}
                      className="btn-primary !px-3 !py-2 text-xs"
                      aria-label="Save caption"
                      data-testid={`admin-gallery-caption-save-${p.id}`}
                    >
                      <Save className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between gap-1">
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleMove(i, -1)}
                      disabled={i === 0}
                      className="w-9 h-9 inline-flex items-center justify-center border border-linen text-ink/60 hover:border-forest hover:text-forest disabled:opacity-30 transition-colors"
                      aria-label={`Move photo ${i + 1} earlier`}
                      data-testid={`admin-gallery-move-left-${p.id}`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleMove(i, 1)}
                      disabled={i === photos.length - 1}
                      className="w-9 h-9 inline-flex items-center justify-center border border-linen text-ink/60 hover:border-forest hover:text-forest disabled:opacity-30 transition-colors"
                      aria-label={`Move photo ${i + 1} later`}
                      data-testid={`admin-gallery-move-right-${p.id}`}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { setReplaceTarget(p.id); replaceInputRef.current?.click(); }}
                      className="w-9 h-9 inline-flex items-center justify-center border border-linen text-ink/60 hover:border-forest hover:text-forest transition-colors"
                      aria-label={`Replace photo ${i + 1} with a different image`}
                      data-testid={`admin-gallery-replace-${p.id}`}
                    >
                      <Repeat className="w-4 h-4" />
                    </button>
                  </div>
                  <button
                    onClick={() => handleDelete(p)}
                    className={`h-9 px-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide border transition-colors ${
                      confirmDelete === p.id
                        ? "bg-red-700 text-white border-red-700"
                        : "border-linen text-ink/50 hover:border-red-300 hover:text-red-700"
                    }`}
                    data-testid={`admin-gallery-delete-${p.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {confirmDelete === p.id ? "Sure?" : "Remove"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <input type="file" accept="image/*" className="hidden" ref={replaceInputRef} onChange={handleReplacePicked} data-testid="admin-gallery-replace-input" />
    </div>
  );
}
